import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Link2, ShieldCheck, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { fmtCurrency } from '../../shared/lib/format';
import { howDemo, tenDigitCells } from '../../shared/milhar/howItWorksView';
import { normalizeWindow, payoutLadder } from '../../shared/milhar/rulesWindow';
import { MILHAR_TIERS, type MilharTierId } from '../../shared/milhar/tiers';
import { fetchRoundByNumber } from '../archive/api';
import { drawDigits } from '../verify/roundDigits';
import type { MilharStatus } from './api';
import { MatchLadderRows } from './MatchLadderRows';

/**
 * 「How it works」—— 奖券页 HowItWorksSheet 的同一副骨架（`srt-how-*`），三步各一张图
 * 一句话：你选号 → 号码哪来的 → 怎么算中（阶梯图 + 一个反例）。
 *
 * <p><b>一个数字都不写死。</b>票价、倍数、取位窗口、奖池、开奖频次全从 `status`
 * 与 `MILHAR_TIERS` 来；示例号码由最近一期真实开奖号推导（howItWorksView.ts）——
 * 页脚写着「可去链上核对」，示例就不能是一个从没开出过的号。没有已开奖的期时
 * 示例格子留空、页脚不渲染，而不是编一个。
 *
 * <p>十位 D 数字走 `fetchRoundByNumber`，query key 与核对弹层完全一致，命中同一份缓存。
 */
export function MilharHowSheet({ tier, status, onClose, onVerify }: {
  tier: MilharTierId;
  status: MilharStatus | undefined;
  onClose: () => void;
  onVerify: (round: { round: number; label: string; result: string }) => void;
}) {
  const t = useI18nStore((state) => state.t);
  const spec = MILHAR_TIERS.find((entry) => entry.id === tier) ?? MILHAR_TIERS[0];
  const config = status?.config;
  const pool = status?.pool;
  const currency = pool?.currency ?? 'BRL';
  const digits = Number(config?.digits ?? spec.digits);
  const price = Number(config?.ticketPrice ?? NaN);
  const window = normalizeWindow(config?.digitWindow, tier);
  const last = status?.recentResults?.[0];
  const result = last?.result && last.result.length === digits ? last.result : '';

  const archive = useQuery({
    queryKey: ['milhar-verify-round', last?.round ?? 0],
    queryFn: () => fetchRoundByNumber(last!.round),
    enabled: !!last,
    staleTime: 5 * 60_000,
  });
  const tenCells = tenDigitCells(archive.data ? drawDigits(archive.data) : '', result, window);
  const demo = howDemo(result);
  const ladder = payoutLadder(config?.multipliers, digits, price);
  const money = (value: number) => fmtCurrency(value, currency);
  const prizeFor = (hits: number) => {
    const row = ladder.find((entry) => entry.matched === hits);
    return row?.payout != null ? money(row.payout) : '';
  };
  const hitsLabel = (hits: number, value: string) => (hits === 1
    ? t('milharHowHit1').replace('%v%', value)
    : t('milharHowHits').replace('%n%', String(hits)).replace('%v%', value));
  const title = t(spec.titleKey);
  const priceText = Number.isFinite(price) ? money(price) : '';

  const cell = (char: string, cls: string, key: number, tag?: string) => (
    <span key={key} className={`srt-how-cell milhar-how-cell ${cls}`}>
      {tag ? <em>{tag}</em> : null}
      {char || '·'}
    </span>
  );
  // 从右往左点亮 hits 位，紧挨着的那一位画成「断」，再往左的全灰 —— 灰的也许相同，
  // 但不给它任何颜色：给了就像它有价值，而这正是这一段要纠正的误解。
  const compareRow = (pick: string, hits: number, brokenAt: number | null) =>
    pick.split('').map((char, i) => cell(
      char,
      i >= digits - hits ? 'is-rank1' : brokenAt === i ? 'is-miss' : 'is-dim',
      i,
    ));

  const sheet = (
    <div className="srt-how-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="srt-how" role="dialog" aria-modal="true" aria-label={t('milharHowRow')}
        onMouseDown={(e) => e.stopPropagation()}>
        <header className="srt-how-head">
          <div>
            <h2>{t('milharHowRow')}</h2>
            <span className="milhar-how-sub">
              {title}{priceText ? ` · ${t('milharHowPerBet').replace('%v%', priceText)}` : ''}
            </span>
          </div>
          <button className="srt-how-x" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        <div className="srt-how-body">
          <ol className="srt-how-steps">
            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">1</span>
              <div className="srt-how-step-body">
                <div className="srt-how-strip" aria-hidden="true">
                  {Array.from({ length: digits }, (_, i) => cell(
                    demo ? demo.yours[i] : '', 'is-you', i, i === 0 ? t('milharHowYou') : undefined,
                  ))}
                  {priceText ? <span className="srt-how-cell milhar-how-cell is-price">{priceText}</span> : null}
                </div>
                <p>{t('milharHowS1').replace('%d%', String(digits)).replace('%c%', t(spec.drawPhraseKey))}</p>
              </div>
            </li>

            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">2</span>
              <div className="srt-how-step-body">
                <div className="srt-how-chip" aria-hidden="true">
                  <Link2 size={14} aria-hidden="true" />
                  <span>Ethereum</span>
                  <b>{t('milharHowChip')}</b>
                </div>
                <div className="milhar-how-dgrid" aria-hidden="true">
                  {tenCells.map((d, i) => (
                    <span key={i} className={`milhar-how-d${d.inWindow ? ' is-window' : ''}`}>
                      {d.digit || '·'}
                      <small>D{i + 1}</small>
                    </span>
                  ))}
                </div>
                {result ? (
                  <div className="srt-how-math" aria-hidden="true">
                    <span>{t('milharHowDerive').replace('%a%', String(window[0])).replace('%b%', String(window[1]))}</span>
                    <i><ChevronRight size={13} aria-hidden="true" /><span className="is-win mono">{result}</span></i>
                  </div>
                ) : null}
                <p>{t('milharHowS2')
                  .replace('%t%', title)
                  .replace('%a%', String(window[0]))
                  .replace('%b%', String(window[1]))}</p>
              </div>
            </li>

            <li className="srt-how-step">
              <span className="srt-how-step-n" aria-hidden="true">3</span>
              <div className="srt-how-step-body">
                {/* 大厅卡片上的同一张阶梯图（MatchLadderRows），放大一号：从末位往左点亮
                    几格就拿哪一档，规则和赔付一张图讲完。玩家在大厅学到的那幅画，到了
                    这里还是同一幅。 */}
                <div className="milhar-how-ladder" aria-hidden="true">
                  <MatchLadderRows
                    digits={digits}
                    rows={ladder}
                    drawn={result || null}
                    jackpot={pool ? Number(pool.takeHome) : null}
                    currency={currency}
                    size="lg"
                  />
                </div>
                {/* 反例是这一步唯一多出来的图：前面全对、倒数第二位错，只算 1 位。玩家
                    默认按「对上几个数字」理解，阶梯图本身纠正不了这个误解。 */}
                {demo ? (
                  <div className="milhar-how-trap" aria-hidden="true">
                    <div className="srt-how-strip">
                      <span className="milhar-how-rowlab is-you">{t('milharHowYou')}</span>
                      {compareRow(demo.broken, demo.brokenHits, digits - 2)}
                    </div>
                    <div className="srt-how-math">
                      <span>{t('milharHowOnly').replace('%s%', hitsLabel(demo.brokenHits, prizeFor(demo.brokenHits)))}</span>
                    </div>
                  </div>
                ) : null}
                <p>{t('milharHowS3')}</p>
              </div>
            </li>
          </ol>
        </div>

        {last ? (
          <footer className="srt-how-foot">
            <button className="srt-how-verify" type="button"
              onClick={() => onVerify({ round: last.round, label: last.label, result: last.result })}>
              <ShieldCheck size={15} aria-hidden="true" />
              <span>{t('milharHowVerify').replace('%r%', last.label || `#${last.round}`)}</span>
              <ChevronRight size={15} aria-hidden="true" />
            </button>
          </footer>
        ) : null}
      </section>
    </div>
  );
  return typeof document === 'undefined' ? sheet : createPortal(sheet, document.body);
}
